/**
 * Which snap toggles mean anything for what you're currently doing (#364).
 *
 * The snap bar shows seven chips at all times, but most of them are inert in
 * most tools: Point-constraint pins a coincident constraint, which only
 * construction has; Line snap is consulted while DRAWING and never while moving
 * a selection; Center means "draw from the middle out", which a freehand stroke
 * has no middle for. A chip that looks armed and does nothing is worse than no
 * chip, because the user reasonably concludes the snap itself is broken.
 *
 * ## Why this is a table of GESTURES, not of tools
 *
 * The obvious shape — one row per tool — is wrong twice over. It's too coarse,
 * because a single tool changes its answer with the draw kind (a rectangle
 * offers the size lock, a freehand stroke offers nothing). And it's redundant,
 * because the leather Shape, Cutout and Stitch tools, and construction's Draw
 * mode, all resolve the SAME kinds through the same branches — four tools, one
 * set of rules.
 *
 * What actually decides the answer is the GESTURE in progress. There are only a
 * dozen of those across the whole app, and every tool / sub-tool / sub-sub-tool
 * / draw kind maps onto one:
 *
 * | Gesture | What it is | Snaps it consults |
 * |---|---|---|
 * | `none`            | nothing is being positioned | — |
 * | `placePoint`      | a single point (a hole)     | point, line, grid |
 * | `drawBox`         | rect / ellipse drag         | + sizes, center |
 * | `drawAxis`        | pill / slot drag            | + angle, center |
 * | `drawLine`        | one straight segment        | + angle |
 * | `drawPolyline`    | click-to-add open path      | + angle, sizes |
 * | `drawPolygon`     | click-to-add closed path    | + angle, sizes, center |
 * | `drawFreehand`    | sampled stroke              | point, line, grid (start only) |
 * | `transformObject` | move / rotate / resize      | angle, grid, point, sizes, center |
 * | `editVertex`      | drag an anchor (construction) | angle, grid, point |
 * | `editOutline`     | drag an outline point (leather) | angle, grid |
 * | `cutStroke`       | knife / delete swipe        | angle, point, line, grid |
 *
 * Adding a tool then means answering one question — "which of these gestures is
 * it?" — instead of filling in seven booleans, and a tool that reuses an
 * existing gesture needs no new rules at all.
 *
 * ## The rule the table follows
 *
 * A toggle is inapplicable ONLY when no code path reachable from the active
 * gesture reads its gate. That is deliberately conservative, and the direction
 * matters: dimming a chip that DOES work is a real regression (the user stops
 * reaching for a snap that would have fired), while failing to dim an inert one
 * merely leaves today's behaviour. So an unrecognised tool maps to no gesture
 * and everything stays lit.
 *
 * ## What this does NOT do
 *
 * It doesn't change what snapping does, and it doesn't touch the stored
 * setting. An inapplicable toggle that is ON stays ON — it simply isn't
 * consulted — so switching back to a tool where it applies finds it exactly as
 * the user left it. The UI dims such a chip and explains why, but keeps it
 * clickable, so a snap can still be armed for the tool you're about to pick.
 */

/** The seven snap chips, excluding the master switch. */
export type SnapChildKey =
  | "snapAngle"
  | "snapSizes"
  | "snapCenter"
  | "snapGrid"
  | "snapPoint"
  | "snapLine"
  | "snapPointConstraint";

/** Enough of the editor's mode to identify the gesture. Assembled by `app.ts`,
 *  the only place that knows the active tool and its sub-state. */
export interface SnapToolContext {
  /** Active tool id — "shape", "cutout", "hole", "stitch", "select",
   *  "outlineEdit", "construction", "stamp", … */
  toolId: string | null;
  /** Construction sub-mode ("draw", "select", "edit", "delete", "offset",
   *  "simple-offset", "mirror", "simple-mirror", "round"). Loose string so this
   *  table needn't change every time the tool grows one. */
  subMode?: string | null;
  /** Construction edit action ("drag", "smooth-toggle", "add-point", "delete",
   *  "slice") — only "slice" changes the answer. */
  editAction?: string | null;
  /** The draw kind the active drawing tool is set to. */
  drawKind?: string | null;
}

/** The gestures the whole app reduces to. See the table in the module note. */
export type SnapGesture =
  | "none"
  | "placePoint"
  | "drawBox"
  | "drawAxis"
  | "drawLine"
  | "drawPolyline"
  | "drawPolygon"
  | "drawFreehand"
  | "transformObject"
  | "editVertex"
  | "editOutline"
  | "cutStroke";

/** The placement tiers — `resolveDrawSnap`'s vertex / crossing / projection /
 *  grid pipeline. Anything that puts a point on the canvas consults these. */
const PLACEMENT: SnapChildKey[] = ["snapPoint", "snapLine", "snapGrid"];

/**
 * What each gesture consults. Every entry is the set its code path actually
 * reads — `snapPointConstraint` is filtered separately, since it exists only in
 * construction whatever the gesture.
 */
const GESTURE_GATES: Record<SnapGesture, ReadonlySet<SnapChildKey>> = {
  // Stamping, filling, tracing, seating hardware, deleting, mirroring,
  // rounding, and any derived (offset) shape: no gate is read at all. Either
  // nothing is being positioned, or the result is derived from something you
  // pick rather than a point you place.
  none: new Set(),
  // HoleTool: a hole is a point. Nothing to rotate, size or grow from a middle.
  placePoint: new Set(PLACEMENT),
  // `constrainSquare` for the square / circle lock, and `resolveBoundingBox`'s
  // draw-from-center. No angle: an axis-aligned box has no axis to rotate.
  drawBox: new Set([...PLACEMENT, "snapSizes", "snapCenter"] as SnapChildKey[]),
  // A pill is drawn ALONG an axis, so `snapToCommonAngle` gates that drag —
  // and it still builds a box, so draw-from-center applies. No square lock.
  drawAxis: new Set([
    ...PLACEMENT,
    "snapAngle",
    "snapCenter",
  ] as SnapChildKey[]),
  // One segment: an angle to lock, but no box and no size to match.
  drawLine: new Set([...PLACEMENT, "snapAngle"] as SnapChildKey[]),
  // Click-to-add through the draft engine: each segment's angle, and the Size
  // snap making every side match the first. Open, so no center.
  drawPolyline: new Set([
    ...PLACEMENT,
    "snapAngle",
    "snapSizes",
  ] as SnapChildKey[]),
  // As polyline, plus the regular-polygon gesture's circumscribed radius, which
  // is measured from the center.
  drawPolygon: new Set([
    ...PLACEMENT,
    "snapAngle",
    "snapSizes",
    "snapCenter",
  ] as SnapChildKey[]),
  // "Freehand doesn't snap intermediate samples — it would fight the smoothing.
  // Only the start vertex (in onDown) snaps." (ShapeTool). That start vertex
  // still runs the placement tiers, so they stay applicable.
  drawFreehand: new Set(PLACEMENT),
  // SelectTool + its TransformController, and construction's Select mode
  // (selectEditController / rotationController / resizeController). Point comes
  // via `updateLiveSnap` (gated on `isPointSnapActive`); sizes and center come
  // from the resize handles' aspect lock and resize-from-center. A dragged
  // resize handle also lands on an edge or a crossing of two — construction's
  // through `snapHandleDrag` (#1169), leather's through `resolveDrawSnap`
  // (#1174) — so Line snap applies.
  transformObject: new Set([
    "snapAngle",
    "snapGrid",
    "snapPoint",
    "snapLine",
    "snapSizes",
    "snapCenter",
  ]),
  // construction's vertexEditController: angle + grid directly, point through
  // `updateLiveSnap`. A vertex drag sizes no box.
  editVertex: new Set(["snapAngle", "snapGrid", "snapPoint"]),
  // leather's outlineEditController reads angle + grid and nothing else — it
  // has no live-snap channel, so a dragged outline point doesn't catch other
  // geometry.
  editOutline: new Set(["snapAngle", "snapGrid"]),
  // `knifeStroke`: angle plus the placement tiers. It isn't sizing a box.
  cutStroke: new Set([...PLACEMENT, "snapAngle"] as SnapChildKey[]),
};

/** Draw kind → gesture. Shared by the leather Shape / Cutout / Stitch tools and
 *  construction's Draw mode, which resolve these kinds through the same
 *  branches — one mapping covers all four tools. */
const KIND_GESTURE: Record<string, SnapGesture> = {
  rect: "drawBox",
  ellipse: "drawBox",
  slot: "drawAxis",
  line: "drawLine",
  polyline: "drawPolyline",
  polygon: "drawPolygon",
  freehand: "drawFreehand",
  // Derived from a source edge and a distance by `offsetController` /
  // `outlineOffsetController`, neither of which reads a gate.
  offset: "none",
};

/** Leather tools that draw a shape, and so take their gesture from the kind. */
const KIND_DRIVEN_TOOLS = new Set(["shape", "cutout", "stitch"]);

/** Tools checked and found to read no gate at all. */
const NO_SNAP_TOOLS = new Set([
  "stamp",
  "lineStamp",
  "hardware",
  "fill",
  "region",
  "trace",
  "freePath",
  "leatherDelete",
  "leatherMirror",
  "leatherRound",
]);

/** Construction sub-modes whose whole gesture goes to a controller that never
 *  asks: offset derives from a source and a distance, mirror reflects across a
 *  picked axis, round drags a radius along a corner bisector. */
const CONSTRUCTION_NO_SNAP_SUBMODES = new Set([
  "offset",
  "simple-offset",
  "mirror",
  "simple-mirror",
  "round",
]);

/**
 * The gesture the editor is currently set up for, or `null` when the tool isn't
 * one this table has been checked against — in which case nothing is dimmed.
 *
 * Exported because it is the thing worth testing: get the gesture right and the
 * seven answers follow from one table.
 */
export function snapGestureFor(ctx: SnapToolContext): SnapGesture | null {
  const tool = ctx.toolId;
  if (!tool) return null;

  if (NO_SNAP_TOOLS.has(tool)) return "none";
  if (tool === "hole") return "placePoint";
  if (tool === "select") return "transformObject";
  if (tool === "outlineEdit") return "editOutline";
  if (tool === "leatherSlice") return "cutStroke";
  // The ruler (#155) positions one straight segment between two points, which
  // is `drawLine` exactly: point / line / grid to land the ends, angle to hold
  // a direction. That it measures rather than draws changes nothing about
  // which snaps it consults.
  if (tool === "measure") return "drawLine";

  if (KIND_DRIVEN_TOOLS.has(tool)) {
    return KIND_GESTURE[ctx.drawKind ?? ""] ?? null;
  }

  if (tool === "construction") {
    const mode = ctx.subMode ?? "draw";
    if (CONSTRUCTION_NO_SNAP_SUBMODES.has(mode)) return "none";
    // Slice is an Edit action; Delete is its own sub-mode. Both run
    // `knifeStroke` through `deleteSliceController`.
    if (mode === "delete") return "cutStroke";
    if (mode === "edit")
      return ctx.editAction === "slice" ? "cutStroke" : "editVertex";
    if (mode === "select") return "transformObject";
    if (mode === "draw") return KIND_GESTURE[ctx.drawKind ?? ""] ?? null;
    return null; // a sub-mode added since — don't guess
  }

  return null; // a tool added since — don't guess
}

const isConstruction = (ctx: SnapToolContext) => ctx.toolId === "construction";

/**
 * Does `key` do anything right now? `true` whenever the gesture is unknown, so
 * a tool added without updating this table keeps every chip live.
 */
export function isSnapApplicable(
  key: SnapChildKey,
  ctx: SnapToolContext,
): boolean {
  // Pinning records a coincident CONSTRAINT, which only construction lines
  // have — `isPointConstraintActive` is read by construction-side controllers
  // only, whatever the gesture.
  if (key === "snapPointConstraint" && ctx.toolId && !isConstruction(ctx)) {
    return false;
  }
  const gesture = snapGestureFor(ctx);
  if (!gesture) return true;
  if (key === "snapPointConstraint") {
    // Construction: the pin rides along with whatever consults point snap.
    return GESTURE_GATES[gesture].has("snapPoint");
  }
  return GESTURE_GATES[gesture].has(key);
}

/** Why a chip is inert, for its tooltip. Null when it applies. Phrased as what
 *  the snap needs rather than what the tool lacks, so it reads as guidance. */
export function snapInapplicableReason(
  key: SnapChildKey,
  ctx: SnapToolContext,
): string | null {
  if (isSnapApplicable(key, ctx)) return null;
  const drawing = snapGestureFor(ctx)?.startsWith("draw") ?? false;
  switch (key) {
    case "snapPointConstraint":
      return "Pinning a point applies to construction lines";
    case "snapPoint":
      return "Point snap applies while placing points";
    case "snapLine":
      return "Line snap applies while placing points";
    case "snapGrid":
      return "Grid snap applies while drawing or moving";
    case "snapAngle":
      return drawing
        ? "Angle snap applies to lines, pills and multi-point shapes"
        : "Angle snap applies while drawing or moving";
    case "snapSizes":
      return drawing
        ? "Size snap applies to rectangles, ellipses and multi-point shapes"
        : "Size snap applies while drawing shapes or resizing";
    case "snapCenter":
      return drawing
        ? "Draw from center applies to shapes with a bounding box"
        : "Draw from center applies while drawing shapes or resizing";
  }
}

/**
 * The round-corner "all vs single" toggle rides in the same pill, and is
 * narrower than any chip: `settings.roundAllCorners` is read by exactly two
 * controllers — `leatherRoundController` (the Round tool) and `roundController`
 * (construction's Round sub-mode).
 */
export function isRoundModeApplicable(ctx: SnapToolContext): boolean {
  if (!ctx.toolId) return true;
  return (
    ctx.toolId === "leatherRound" ||
    (isConstruction(ctx) && ctx.subMode === "round")
  );
}

/** Tooltip suffix for the round toggle when it decides nothing. */
export function roundModeInapplicableReason(
  ctx: SnapToolContext,
): string | null {
  return isRoundModeApplicable(ctx)
    ? null
    : "Applies while rounding corners with the Round tool";
}
